import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { PlayerIndex } from '@tpubg/shared';
import { AdSlot } from '../ads/AdSlot';
import { FullscreenButton } from '../components/FullscreenButton';
import { GameActions } from '../components/LikeButton';
import { ModePicker, type PlayMode } from '../components/ModePicker';
import { NextUp } from '../components/NextUp';
import { sound } from '../lib/audio';
import { CPU_LEVEL_LABEL, CPU_PLAYER, parseLevel } from '../lib/cpu';
import { useCpuLabels } from '../lib/useCpuLabels';
import { PLAYER_STYLES } from '../lib/players';
import { PlayerTag, useAutoFocus } from '../ui/Overlays';
import type { CatalogEntry } from '../games/catalog';
import type { TurnGameDef, TurnResult } from './types';
import { setGameMeta } from '../lib/seo';

/** Pass-and-play wrapper: turn indicator, win tally, results and alternating first player. */
export function TurnShell({ entry, def }: { entry: CatalogEntry; def: TurnGameDef }) {
  const [params, setParams] = useSearchParams();
  const mode: PlayMode = { vsCpu: !!def.cpu && params.get('vs') !== '2p', level: parseLevel(params.get('level')) };
  const cpu = mode.vsCpu ? { player: CPU_PLAYER, level: mode.level } : null;
  const modeKey = mode.vsCpu ? `cpu-${mode.level}` : 'pvp';
  useCpuLabels(cpu?.player ?? null);
  const [gameNo, setGameNo] = useState(0);
  const starter = (gameNo % 2) as PlayerIndex;
  const [turn, setTurn] = useState<PlayerIndex>(starter);
  const [tally, setTally] = useState<[number, number, number]>([0, 0, 0]);
  const [result, setResult] = useState<TurnResult | null>(null);
  const finished = useRef(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const fitRef = useRef<HTMLDivElement>(null);
  useFitInFullscreen(stageRef, fitRef);

  useEffect(() => {
    setGameMeta(entry);
  }, [entry.name]);

  const onFinish = useCallback((r: TurnResult) => {
    if (finished.current) return;
    finished.current = true;
    setResult(r);
    setTally((t) => (r.winner === null ? [t[0], t[1], t[2] + 1] : r.winner === 0 ? [t[0] + 1, t[1], t[2]] : [t[0], t[1] + 1, t[2]]));
    sound.play(r.winner === null ? 'round' : 'match');
  }, []);

  const newGame = () => {
    finished.current = false;
    setResult(null);
    setGameNo((n) => n + 1);
    setTurn(((gameNo + 1) % 2) as PlayerIndex);
  };

  const changeMode = (m: PlayMode) => {
    setParams(m.vsCpu ? { vs: 'cpu', level: m.level } : { vs: '2p' }, { replace: true });
    finished.current = false;
    setResult(null);
    setTally([0, 0, 0]);
    setGameNo(0);
    setTurn(0);
  };

  const nameOf = (i: PlayerIndex) => (cpu ? (i === cpu.player ? 'Bot' : 'You') : PLAYER_STYLES[i].label);

  const play = useCallback((n: Parameters<typeof sound.play>[0]) => sound.play(n), []);
  const Game = def.Component;

  const chip = (i: PlayerIndex) => {
    const s = PLAYER_STYLES[i];
    const active = !result && turn === i;
    return (
      <div className={`turn-chip turn-chip--p${i + 1}${active ? ' is-active' : ''}`}>
        <span className="turn-chip__tag">
          <span aria-hidden="true">{s.symbol}</span> {s.label}
          {cpu && nameOf(i) !== s.label && <span className="turn-chip__who"> · {nameOf(i)}</span>}
        </span>
        <span className="turn-chip__wins">
          {tally[i]} {tally[i] === 1 ? 'win' : 'wins'}
        </span>
      </div>
    );
  };

  return (
    <main className="page turn-page game-page" id="main">
      <div className="turn-head">
        <Link to="/" className="btn btn--ghost btn--small turn-head__back">
          ← All games
        </Link>
        <span className="game-icon" aria-hidden="true">
          <entry.Icon />
        </span>
        <div className="turn-head__title">
          <h1>{entry.name}</h1>
          <p className="muted small">
            {entry.categoryLabel} · {cpu ? `You vs the Bot (${CPU_LEVEL_LABEL[cpu.level]})` : 'Pass and play on one screen'}
          </p>
        </div>
      </div>
      <div className="game-stage" ref={stageRef}>
      <div className="game-fit" ref={fitRef}>

      {def.cpu && <ModePicker value={mode} onChange={changeMode} twoPlayerLabel="2 Players" compact />}

      <div className="turn-bar">
        {chip(0)}
        <p className="turn-bar__status" aria-live="polite">
          {result ? (
            result.winner === null ? (
              'Draw!'
            ) : cpu ? (
              result.winner === cpu.player ? (
                'The Bot wins!'
              ) : (
                'You win!'
              )
            ) : (
              <>
                <PlayerTag index={result.winner} /> wins!
              </>
            )
          ) : cpu && turn === cpu.player ? (
            <span className="thinking">
              <PlayerTag index={turn} name="Bot" /> is thinking…
            </span>
          ) : cpu ? (
            <>
              <PlayerTag index={turn} name="Your" /> turn
            </>
          ) : (
            <>
              <PlayerTag index={turn} />’s turn
            </>
          )}
        </p>
        {chip(1)}
      </div>

      <div className="turn-board">
        <Game key={`${modeKey}-${gameNo}`} starter={starter} onTurn={setTurn} onFinish={onFinish} play={play} cpu={cpu} />
        {result && <ResultPanel entry={entry} result={result} onAgain={newGame} draws={tally[2]} cpuPlayer={cpu?.player ?? null} />}
      </div>
      </div>
        <div className="game-bar">
          <span className="game-bar__name">{entry.name}</span>
          <GameActions gameId={entry.id} path={entry.path} />
          <FullscreenButton target={stageRef} compact />
        </div>
      </div>

      <div className="turn-foot">
        <button type="button" className="btn btn--ghost btn--small" onClick={newGame}>
          New game
        </button>
        <details className="turn-rules">
          <summary>How to play</summary>
          <ul className="rules">
            {def.rules.map((r) => (
              <li key={r}>{r}</li>
            ))}
            <li>The first move alternates between games.</li>
          </ul>
        </details>
      </div>
      <AdSlot placement="board" />
    </main>
  );
}

/**
 * Board games have fixed-size layouts, so in fullscreen the whole game is scaled
 * (up or down) to fill the screen above the bottom bar. Uses a transform, which
 * hit-testing and getBoundingClientRect both respect, so clicks still land.
 */
function useFitInFullscreen(stageRef: RefObject<HTMLDivElement | null>, fitRef: RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const stage = stageRef.current;
    const fit = fitRef.current;
    if (!stage || !fit) return;
    const fs = () => document.fullscreenElement === stage || stage.classList.contains('is-pseudo-fullscreen');
    const apply = () => {
      fit.style.transform = '';
      fit.style.height = '';
      fit.style.width = '';
      if (!fs()) return;
      const bar = stage.querySelector<HTMLElement>(':scope > .game-bar');
      const availH = stage.clientHeight - (bar?.offsetHeight ?? 0) - 48;
      const availW = stage.clientWidth - 32;
      const w = fit.scrollWidth;
      const h = fit.scrollHeight;
      if (!w || !h) return;
      const scale = Math.min(1.8, availW / w, availH / h);
      fit.style.width = `${w}px`;
      fit.style.transform = `scale(${scale})`;
      fit.style.height = `${h * scale}px`;
    };
    const ro = new ResizeObserver(() => requestAnimationFrame(apply));
    ro.observe(stage);
    document.addEventListener('fullscreenchange', apply);
    const mo = new MutationObserver(apply);
    mo.observe(stage, { attributes: true, attributeFilter: ['class'] });
    return () => {
      ro.disconnect();
      mo.disconnect();
      document.removeEventListener('fullscreenchange', apply);
    };
  }, [stageRef, fitRef]);
}

function ResultPanel({ entry, result, onAgain, draws, cpuPlayer }: { entry: CatalogEntry; result: TurnResult; onAgain: () => void; draws: number; cpuPlayer: PlayerIndex | null }) {
  const ref = useAutoFocus<HTMLButtonElement>();
  const w = result.winner;
  return (
    <div className="overlay turn-result" role="dialog" aria-label="Game over">
      <div className={`panel panel--${w === null ? 'neutral' : w === 0 ? 'p1' : 'p2'}`}>
        <h2 className="panel__title">
          {w === null ? 'It’s a draw' : cpuPlayer === null ? `${PLAYER_STYLES[w].label} wins!` : w === cpuPlayer ? 'The Bot wins' : 'You win! 🎉'}
        </h2>
        {result.text && <p className="panel__lead">{result.text}</p>}
        {draws > 0 && <p className="panel__status">Draws so far: {draws}</p>}
        <div className="panel__actions">
          <button ref={ref} type="button" className="btn btn--primary" onClick={onAgain}>
            Play again
          </button>
          <Link to="/" className="btn btn--ghost">
            All games
          </Link>
        </div>
        <NextUp entry={entry} panel />
      </div>
    </div>
  );
}
